<template>
  <div>
    <LoadingIndicator v-if="isLoading" />
    <div v-if="system">
      <div class="d-flex justify-content-between p-2">
        <div>
          <el-button v-if="!hideBackButton" type="default" @click="$router.back()">
            <i class="el-icon-back" />
            <span v-t="'general.back'" />
          </el-button>
        </div>
        <el-button type="primary" @click="onAddToCart">
          <i class="el-icon-shopping-cart-1" />
          <span v-t="'general.add_to_cart'" />
        </el-button>
      </div>
      <div class="grid-container pt-3">
        <el-card :body-style="{ padding: '0px' }" class="my-1">
          <img
            v-if="system.get('image')"
            style="min-height: 110px; object-fit: contain"
            class="w-50"
            :src="GENERAL.IMAGE_PATH + system.get('image')"
          />
          <div v-if="system.get('ecoLine')" class="text-right px-3">
            <img width="30" src="@/assets/images/misc/viacor-greenlabel_80.png" />
          </div>
          <div style="padding: 14px;">
            <span
              class="font-color-primary h5"
              v-html="
                system.get('name').replace('GREEN LINE ECO', `<span style='color: #9bc524'>GREEN LINE ECO</span>`)
              "
            />
            <div class="bottom clearfix mt-2" v-html="system.get('description')" />
            <div>
              <span v-if="linkList.length !== 0" class="font-color-primary font-weight-bold">{{
                $t('general.further_informations')
              }}</span>
              <el-link
                v-for="(link, index) in linkList"
                :key="'link' + index"
                class="text-center mt-1 pb-1 border-bottom d-block w-100"
              >
                <span @click="onExternalLinkClick(link.link)" v-html="link.label" />
              </el-link>
            </div>
          </div>
        </el-card>
        <el-card class="my-1">
          <slot name="header">
            <span class="font-color-primary font-weight-bold">{{ $tc('general.attribute', 2) }}</span>
          </slot>
          <div class="py-3">
            <el-tag v-for="group in system.get('attributes')" :key="group.id" class="m-1" size="mini">
              {{ group.get('name') }}
            </el-tag>
          </div>
        </el-card>
        <el-card class="my-1">
          <div class="font-color-primary font-weight-bold mb-3">
            {{ $tc('systems.system_advantage', 2) }}
          </div>
          <div class="text-left" v-html="systemInformation" />
        </el-card>
        <el-card class="my-1">
          <slot name="header">
            <span class="font-color-primary font-weight-bold">{{ $tc('products.product', 2) }}</span>
          </slot>
          <div>
            <el-link
              v-for="product in systemProducts"
              :key="product.pos"
              class="text-center mt-1 pb-1 border-bottom d-block"
              @click="onProductSelect(product)"
            >
              <div class="text-uppercase small font-weight-bold font-color-primary">
                {{ product.productGroupName }}
              </div>
              <span>{{ product.productName }}</span>
            </el-link>
            <div v-if="!hasProducts">
              {{ $t('general.no_products') }}
            </div>
          </div>
        </el-card>
        <el-card class="my-1">
          <slot name="header">
            <span class="font-color-primary font-weight-bold">{{ $tc('general.files', 2) }}</span>
          </slot>
          <div>
            <el-link
              v-for="file in systemFiles"
              :key="file.id"
              class="d-block py-2"
              @click="onFileSelect(file)"
              v-html="file.get('name')"
            />
            <div v-if="systemFiles.length === 0">
              {{ $t('general.no_files') }}
            </div>
          </div>
        </el-card>
      </div>
    </div>
  </div>
</template>

<script>
import GENERAL from '@/config/general';
import { mapActions, mapGetters } from 'vuex';
import StoreModules from '../../store/store-modules';
import LoadingIndicator from '../../components/loading/LoadingIndicator';
import AddSystemModal from '../../components/modals/AddSystemModal';
import PDFViewerModal from '../../components/modals/PDFViewerModal';
import DetailProductModal from '../../components/modals/DetailProductModal';
import { Plugins } from '@capacitor/core';
import { parse } from 'node-html-parser';

const { Browser } = Plugins;

export default {
  name: 'SystemView',
  components: { LoadingIndicator },
  props: {
    systemId: {
      type: [Number, String],
      default: -1,
    },
    hideBackButton: {
      type: Boolean,
      default: false,
    },
  },
  mounted() {
    this.loadSystem(parseInt(this.systemId));
  },
  destroyed() {},
  data() {
    return {
      GENERAL,
      linkList: [],
      systemInformation: '',
    };
  },
  computed: {
    ...mapGetters(StoreModules.SYSTEM_DETAIL, ['system', 'isLoading']),
    systemFiles() {
      if (this.system.get('files'))
        return this.system
          .get('files')
          .filter((el) => el.get('path').includes('Systeme'))
          .sort((a, b) => (a.get('mediaTypeId') > b.get('mediaTypeId') ? 1 : -1));
      else return [];
    },
    systemProducts() {
      let s = this.system.get('products') || [];
      return s.sort((a, b) => (a.pos > b.pos ? 1 : -1));
    },
    hasProducts() {
      let products = this.system.get('products');
      if (products) return products.length > 0;
      else return false;
    },
  },
  watch: {
    system() {
      this.newHTML(this.system.get('systemInformation'));
    },
  },
  methods: {
    ...mapActions(StoreModules.SYSTEM_DETAIL, ['loadSystem']),
    onAddToCart() {
      this.$modal.show(
        AddSystemModal,
        { system: this.system },
        { height: 'auto', width: '90%', minWidth: 300, maxWidth: 600 }
      );
    },
    async onFileSelect(file) {
      this.$modal.show(PDFViewerModal, { file }, { height: '80%', width: '90%', minWidth: 300, maxWidth: 600 });
    },
    onProductSelect(product) {
      this.$modal.show(
        DetailProductModal,
        { productId: parseInt(product.productId) },
        { height: 'auto', width: '90%', minWidth: 300, maxWidth: 600 }
      );
    },
    newHTML(html) {
      const root = parse(html);
      let links = root.querySelectorAll('a');

      this.linkList = links
        .map((el) => {
          return {
            label: el.text,
            link: el.getAttribute('href').includes('http')
              ? el.getAttribute('href')
              : `https://viacor.de${el.getAttribute('href')}`,
          };
        })
        .filter((el) => el.label !== '');

      this.systemInformation = root.querySelector('ul');
    },
    async onExternalLinkClick(link) {
      await Browser.open({ url: link });
    },
  },
};
</script>

<style scoped></style>
